import React from 'react';
import PropTypes from 'prop-types';

// Define required fields that cannot be unchecked
const REQUIRED_FIELDS = {
  leads: ['leadContact.name', 'leadContact.email', 'address.city', 'trackingStatus'],
  projects: [],
  clients: []
};

const CustomizeFieldsDialog = ({ isOpen, onClose, columns = [], onToggleColumn = () => {}, onApply = () => {}, type = 'leads' }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedColumns, setSelectedColumns] = React.useState([]);
  const initialized = React.useRef(false);
  const prevIsOpen = React.useRef(false);

  // Get required field keys for current type
  const requiredFields = REQUIRED_FIELDS[type] || [];

  // Update selectedColumns only when dialog opens
  React.useEffect(() => {
    if (!initialized.current || (isOpen && !prevIsOpen.current)) {
      setSelectedColumns(columns);
      initialized.current = true;
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, columns]);

  if (!isOpen) return null;

  const handleToggle = (columnId) => {
    const column = selectedColumns.find(col => col.id === columnId);
    
    // Prevent unchecking required fields
    if (column && column.key && requiredFields.includes(column.key) && column.checked) {
      return; // Don't allow unchecking required fields
    }
    
    const updatedColumns = selectedColumns.map(col => 
      col.id === columnId ? { ...col, checked: !col.checked } : col
    );
    setSelectedColumns(updatedColumns);
    onToggleColumn(columnId);
  };

  const handleApply = () => {
    // Ensure required fields are always checked
    const finalColumns = selectedColumns.map(col => {
      if (col.key && requiredFields.includes(col.key)) {
        return { ...col, checked: true };
      }
      return col;
    });
    
    onApply(finalColumns);
    onClose();
  };

  const handleCancel = () => {
    setSelectedColumns(columns); // Reset to original state
    onClose();
  };

  // Filter columns based on search
  const filteredColumns = selectedColumns.filter(column =>
    column.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get the appropriate title based on type
  const getDialogTitle = () => {
    switch(type) {
      case 'projects':
        return 'Project';
      case 'clients':
        return 'Client';
      case 'leads':
      default:
        return 'Lead';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-xl w-[80%] max-w-4xl min-w-[420px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Customize {getDialogTitle()} Columns
          </h2>
          <button 
            className="text-gray-400 hover:text-gray-500"
            onClick={handleCancel}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-2 border-y border-gray-100">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search columns..."
              className="w-full pl-8 pr-4 py-2 border rounded-lg text-sm"
            />
            <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Column Headers */}
        <div className="flex px-4 py-2 border-b border-gray-100">
          <div className="w-8"></div>
          <div className="flex-1 text-gray-500 text-sm font-medium">Column heading</div>
          <div className="w-24 text-right text-gray-500 text-sm font-medium">Column Status</div>
        </div>

        {/* Column List */}
        <div className="max-h-[460px] overflow-y-auto">
          {filteredColumns.map((column) => {
            const isRequired = column.key && requiredFields.includes(column.key);
            return (
              <div key={column.id} className="flex items-center px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50">
                <div className="w-8">
                  <input 
                    type="checkbox" 
                    checked={column.checked}
                    onChange={() => handleToggle(column.id)}
                    disabled={isRequired && column.checked}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                      isRequired && column.checked ? 'cursor-not-allowed opacity-60' : ''
                    }`}
                  />
                </div>
                <div className="flex-1 text-sm text-gray-900">
                  {column.name}
                  {isRequired && <span className="ml-1 text-red-500">*</span>}
                  {isRequired && column.checked && 
                    <span className="ml-2 text-xs text-gray-500">(Required)</span>
                  }
                </div>
                <div className="w-24 flex justify-end">
                  <div className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    column.active ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-50'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      column.active ? 'bg-green-600' : 'bg-gray-600'
                    }`}></div>
                    {column.active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

CustomizeFieldsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array,
  onToggleColumn: PropTypes.func,
  onApply: PropTypes.func,
  type: PropTypes.oneOf(['leads', 'projects', 'clients'])
};

export default CustomizeFieldsDialog; 